import React, { useContext, useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Badge, Button, Collapse, Form, Input, notification, Popconfirm, Select } from "antd";
import { CloseCircleFilled, DownOutlined, LeftOutlined, MailOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { IAddressDetails, IAddressDetailsPaged } from "@Models/IAddressDetails";
import CountryFlag from "@Components/user/CountryFlag";
import CountrySelect from "@Components/user/CountrySelect";
import AddressService from "@Services/AddressService";
import { MainContext } from "@Context/provider";
import { GroupContext, QueryKeys, UserContext } from "@Constants";
import InspectorModuleTitle from "@Components/groups/InspectorModuleTitle";
import { Countries } from "../../countries";
import InspectorInfoField from "@Components/groups/InspectorInfoField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import SessionManager from "@Core/session";
import { ICreditDetailsPaged } from "@Models/ICreditDetails";
import { ICheckCapFull } from "./types";
import appQueryClient from "@AppQueryClient";

interface IProps {
    context: number;
    instance: string;
    checkCap: ICheckCapFull;
}

const ModuleAddress: React.FC<IProps> = ({ context, instance, checkCap }: IProps) => {
    const { ui: uiState } = useContext(MainContext);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const { Panel } = Collapse;
    const [addresses, setAddresses] = useState<IAddressDetails[]>([]);
    const [current, setCurrent] = useState<IAddressDetails>(null);
    const [fetching, setFetching] = useState<boolean>(false);
    const [updating, setUpdating] = useState<boolean>(false);
    const [isUpdateMode, setIsUpdateMode] = useState(true);
    const stateRef = useRef<IAddressDetails>();
    stateRef.current = current;

    const queryKey = context === GroupContext ? QueryKeys.UserGroupCredits : QueryKeys.GroupUserCredits;
    const query = appQueryClient.getQueryData<ICreditDetailsPaged[]>([queryKey, instance]);
    const credit = query?.find((x) => x.contextId === context && x.instanceId === instance);
    const creditExists = credit !== undefined && credit !== null;

    /**
     * Return the appropriate query key depending on the user or group context of the license.
     */
    const getQueryKey = () => {
        return context === GroupContext ? QueryKeys.UserGroupAddresses : QueryKeys.GroupUserAddresses;
    };

    /**
     * Update the model with edited data.
     */
    const updateField = useMutation(
        async () => {
            setUpdating(true);
            const newAddresses = addresses;
            const index = addresses.findIndex((x) => x.addressId === stateRef.current.addressId);
            if (index !== -1) {
                newAddresses[index] = stateRef.current;
                setAddresses(newAddresses);
            }
            const service = new AddressService();
            const result = await service.update(stateRef.current);
            if (result.hasErrors) {
                notification.success({ message: "Invalid details provided." });
            } else {
                notification.success({ message: "Address updated." });
            }
            setUpdating(false);
        },
        {
            onSuccess: async () => {
                // Force the update in the groups list
                await queryClient.invalidateQueries([getQueryKey()]);
            },
        }
    );

    const updateQueries = async () => {
        // Force the update in the groups list
        if (context === UserContext) await queryClient.invalidateQueries([QueryKeys.UserSearchAddresses]);
        await queryClient.invalidateQueries([getQueryKey()]);
    };

    /**
     * Fetch the addresses for this group or user.
     */
    const fetch = async () => {
        if (instance === null || instance === "") return;
        setFetching(true);
        const service = new AddressService();
        const addresses =
            context === GroupContext ? await service.getGroup([instance]) : await service.getUser([instance]);
        setFetching(false);
        if (!addresses.hasErrors) {
            setIsUpdateMode(true);
            setAddresses(addresses.value);
            if (addresses.value.length > 0 && stateRef.current === null) {
                stateRef.current = addresses.value[0];
                updateRequiredFields();
                if (stateRef.current !== null) {
                    setCurrent(stateRef.current);
                }
            }
        }
    };

    /**
     * Trigger an update to the form data state
     * @param field
     * @param value
     */
    const onInputChange = async (field, value) => {
        const formData = { ...current };
        formData[field] = value;
        setCurrent(formData);
    };

    /**
     * Switch address.
     * @param value
     */
    const onAddressSelect = async (value) => {
        setIsUpdateMode(true);
        stateRef.current = addresses.find((x) => x.addressId === value);
        updateRequiredFields();
        setCurrent(stateRef.current);
    };

    /**
     * Get the label for this address for use in the selector.
     * @param address
     */
    const getLabel = (address: IAddressDetails) => {
        if (address == null) return null;
        let label = "";
        if (address.line1 !== null && address.line1 !== "") {
            label = address.line1;
        } else if (address.line2 !== null && address.line2 !== "") {
            label = address.line2;
        } else if (address.line3 !== null && address.line3 !== "") {
            label = address.line3;
        }

        if (address.postCode !== null && address.postCode !== "") {
            if (label !== "") {
                label += ", ";
            }
            label += address.postCode;
        }
        if (label == "") {
            const index = addresses.findIndex((x) => x.addressId === address.addressId) + 1;
            if (index > 0) {
                label = "Address " + index;
            }
        }
        return label;
    };

    /**
     * Add an address instance.
     */
    const createAddressInstance = useCallback(() => {
        form.resetFields();
        setIsUpdateMode(false);
        const newAddress: IAddressDetails = {
            city: "",
            contextId: context,
            countryCode: "",
            county: "",
            instanceId: instance,
            line1: "",
            line2: "",
            line3: "",
            ownerId: SessionManager.getServiceUser()?.userId,
            postCode: "",
            email: "",
            phone: "",
        };
        stateRef.current = newAddress;
        setCurrent(newAddress);
    }, [context, instance]);

    const handleCancel = () => {
        form.resetFields();
        setIsUpdateMode(true);
        if (addresses.length) {
            stateRef.current = addresses[0];
            updateRequiredFields();
            setCurrent(stateRef.current);
        } else {
            stateRef.current = null;
            setCurrent(stateRef.current);
        }
    };

    const handleSave = async () => {
        if (!isUpdateMode) {
            setUpdating(true);
            const newAddresses = addresses;
            const newAddress = current;
            newAddresses.push(newAddress);
            setAddresses(newAddresses);

            const service = new AddressService();
            const res =
                context == GroupContext
                    ? await service.createGroup(SessionManager.getServiceUser()?.userId, newAddress, instance)
                    : await service.createUser(newAddress, instance);
            setUpdating(false);
            if (!res.hasErrors) {
                newAddress.addressId = res.value.id;
                stateRef.current = newAddress;
                setCurrent(newAddress);
                newAddresses[newAddresses.length - 1].addressId = res.value.id;
                setAddresses(newAddresses);
                await updateQueries();
                setIsUpdateMode(true);
                notification.success({ message: "Address added." });
            } else {
                notification.error({ message: "Error adding address." });
            }
            setUpdating(false);
        } else {
            await updateField.mutate();
        }
    };

    /**
     * Delete the current address instance.
     */
    const deleteAddressInstance = async () => {
        setUpdating(true);

        const service = new AddressService();
        const res = await service.delete(stateRef.current.addressId);
        setUpdating(false);
        if (!res.hasErrors) {
            const newAddresses = addresses.filter((x) => x.addressId !== stateRef.current.addressId);
            setAddresses(newAddresses);
            if (newAddresses.length > 0) {
                stateRef.current = newAddresses[0];
                updateRequiredFields();
                setCurrent(newAddresses[0]);
            } else {
                stateRef.current = null;
                setCurrent(null);
            }
            await fetch();
            await updateQueries();
            notification.success({ message: "Address deleted." });
        } else {
            notification.error({ message: "Error deleting address." });
        }
    };

    /**
     * Fetch all user / group address data.
     */
    useEffect(() => {
        stateRef.current = null;
        setCurrent(null);

        const query = queryClient.getQueryData<IAddressDetailsPaged[]>([getQueryKey()]);
        const addresses = query?.filter((x) => x.contextId === context && x.instanceId === instance);
        if (addresses !== undefined && addresses !== null && addresses.length > 0) {
            setAddresses(addresses);
            stateRef.current = addresses[0];
            if (stateRef.current !== null) {
                setCurrent(stateRef.current);
            }
        } else {
            fetch().then();
        }
    }, [uiState.currentGroup, uiState.currentUser]);

    /**
     * Update required field in the form
     */
    const updateRequiredFields = () => {
        form.setFieldValue("line1", stateRef.current.line1);
        form.setFieldValue("postCode", stateRef.current.postCode);
        form.setFieldValue("countryCode", stateRef.current.countryCode);
        form.setFieldValue("phone", stateRef.current.phone);
    };

    const allowCreate = checkCap(
        context == UserContext ? "Address/CreateUser" : "Address/CreateGroup",
        context,
        instance
    );
    const allowUpdate = checkCap("Address/Update", context, instance);
    const allowDelete = checkCap("Address/Delete", context, instance);

    const addressOptions = Array.isArray(addresses)
        ? addresses.map((x) => {
              return { label: getLabel(x), value: x?.addressId };
          })
        : [];

    return (
        <>
            <Collapse
                destroyInactivePanel={false}
                bordered={false}
                ghost
                expandIconPosition={"end"}
                expandIcon={({ isActive }) => (
                    <Avatar
                        size="small"
                        className={"collapse-icon"}
                        icon={isActive ? <DownOutlined /> : <LeftOutlined />}
                    />
                )}
            >
                <Panel
                    header={
                        <InspectorModuleTitle
                            title={
                                <>
                                    Address{" "}
                                    {addresses?.length > 1 && (
                                        <Badge
                                            className="address-count"
                                            count={addresses?.length}
                                            style={{ marginLeft: 10, backgroundColor: "#00bbff", border: "none" }}
                                        />
                                    )}
                                </>
                            }
                            slug={"address"}
                            fetching={fetching || updating}
                            icon={<MailOutlined />}
                        />
                    }
                    key={1}
                >
                    <Form form={form} className="inspector-form" initialValues={{}} onFinish={handleSave}>
                        {!isUpdateMode && <p>Add new address:</p>}
                        {addresses?.length > 1 && isUpdateMode && (
                            <Form.Item label="Select">
                                <Select
                                    options={addressOptions}
                                    onChange={(newValue) => onAddressSelect(newValue)}
                                    filterOption={false}
                                    defaultValue={{ label: getLabel(current), value: current?.addressId }}
                                    value={{ label: getLabel(current), value: current?.addressId }}
                                />
                            </Form.Item>
                        )}
                        {current !== null && (
                            <>
                                <Form.Item
                                    label="Line 1"
                                    name="line1"
                                    rules={[{ required: true, message: "Please enter an address" }]}
                                >
                                    {allowUpdate ? (
                                        <Input
                                            name="line1"
                                            value={current?.line1}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.line1}</InspectorInfoField>
                                    )}
                                </Form.Item>

                                <Form.Item label="Line 2">
                                    {allowUpdate ? (
                                        <Input
                                            name={"line2"}
                                            value={current?.line2}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.line2}</InspectorInfoField>
                                    )}
                                </Form.Item>

                                <Form.Item label="Line 3">
                                    {allowUpdate ? (
                                        <Input
                                            name={"line3"}
                                            value={current?.line3}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.line3}</InspectorInfoField>
                                    )}
                                </Form.Item>

                                <Form.Item label="City">
                                    {allowUpdate ? (
                                        <Input
                                            name={"city"}
                                            value={current?.city}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.city}</InspectorInfoField>
                                    )}
                                </Form.Item>

                                <Form.Item label="County">
                                    {allowUpdate ? (
                                        <Input
                                            name={"county"}
                                            value={current?.county}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.county}</InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label="Post/zip code"
                                    name="postCode"
                                    rules={[{ required: true, message: "Please enter a post/zip code" }]}
                                >
                                    {allowUpdate ? (
                                        <Input
                                            name={"postCode"}
                                            value={current?.postCode}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.postCode}</InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label={"Country"}
                                    name="countryCode"
                                    rules={[{ required: true, message: "Please enter a country" }]}
                                >
                                    {allowUpdate ? (
                                        <CountrySelect
                                            value={current?.countryCode}
                                            onChange={(value) => onInputChange("countryCode", value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>
                                            <span>
                                                <CountryFlag countryCode={current?.countryCode ?? "GB"} />
                                            </span>
                                            <span style={{ paddingLeft: "10px" }}>
                                                {Countries.get(current?.countryCode ?? "GB")}
                                            </span>
                                        </InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item label="Email">
                                    {allowUpdate ? (
                                        <Input
                                            type={"email"}
                                            name={"email"}
                                            value={current?.email}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.email}</InspectorInfoField>
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label="Phone"
                                    name="phone"
                                    rules={[{ required: true, message: "Please enter a phone number" }]}
                                >
                                    {allowUpdate ? (
                                        <Input
                                            type={"tel"}
                                            name={"phone"}
                                            value={current?.phone}
                                            onChange={(e) => onInputChange(e.target.name, e.target.value)}
                                        />
                                    ) : (
                                        <InspectorInfoField>{current?.phone}</InspectorInfoField>
                                    )}
                                </Form.Item>
                                {allowUpdate ? (
                                    <Form.Item>
                                        {!isUpdateMode ? (
                                            <div className="flex" style={{ gap: "10px" }}>
                                                <Button className="secondary" htmlType="submit" loading={updating}>
                                                    Save
                                                </Button>
                                                <Button className="secondary" onClick={handleCancel} loading={updating}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        ) : (
                                            <Button className="secondary" htmlType="submit" loading={updating}>
                                                Update
                                            </Button>
                                        )}
                                    </Form.Item>
                                ) : null}
                                {allowDelete &&
                                    isUpdateMode &&
                                    (!creditExists || (creditExists && addresses.length > 1)) && (
                                        <>
                                            <Popconfirm
                                                placement="top"
                                                title="Delete this address?"
                                                okText="Yes"
                                                cancelText="Cancel"
                                                className="confirmation"
                                                onConfirm={deleteAddressInstance}
                                                cancelButtonProps={{ className: "secondary" }}
                                            >
                                                <Button
                                                    style={{ width: "100%" }}
                                                    icon={<CloseCircleFilled />}
                                                    loading={updating}
                                                    className="secondary danger"
                                                    title="Delete address"
                                                >
                                                    Delete Address
                                                </Button>
                                            </Popconfirm>
                                            <hr />
                                        </>
                                    )}
                            </>
                        )}
                        {allowCreate && isUpdateMode && (
                            <Button
                                style={{ width: "100%" }}
                                icon={<PlusCircleOutlined />}
                                loading={updating}
                                className="secondary"
                                title="Add address"
                                onClick={createAddressInstance}
                            >
                                Add Address
                            </Button>
                        )}
                    </Form>
                </Panel>
            </Collapse>
            <hr />
        </>
    );
};
export default ModuleAddress;
